import type React from "react"
import { Link } from "react-router-dom"
import { useState } from "react"
import first from "../../assets/Insights/1.svg"
import second from "../../assets/Insights/2.svg"
import third from "../../assets/Insights/3.svg"
import fourth from "../../assets/Insights/4.svg"
import fifth from "../../assets/Insights/5.svg"
import sixth from "../../assets/Insights/6.svg"
import seventh from "../../assets/Insights/7.svg"

type PennyInsightsProps = {}

const PennyInsights: React.FC<PennyInsightsProps> = () => {
    const [expandedInsightIds, setExpandedInsightIds] = useState<number[]>([]);
    
    const insights = [
        {
            id: 1,
            title: "On the Money",
            description:
                "Women are major players in global spending, driving $31.8 trillion and accounting for 75% of discretionary spending by 2028. They often lead financial decisions.",
            icon: first,
            link: "#",
        },
        {
            id: 2,
            title: "Wealth is what you don't see",
            description:
                "For women, money represents a means to achieve a better life and financial security for their families. They prioritise financial education, with 82% of women describing themselves as lifelong learners.",
            icon: second,
            link: "#",
        },
        {
            id: 3,
            title: "The more you know",
            description:
                "Women are committed to ongoing financial education, with 82% describing themselves as lifelong learners, highlighting the importance of continued learning and engagement in financial matters.",
            icon: third,
            link: "#",
        },
        {
            id: 4,
            title: "Everyone else first, then me",
            description:
                "Irish women rank themselves 4th, after family, partner, work. Almost all of the women, irrespective of their relationship status - prioritise everyone else, then themselves.",
            icon: fourth,
            link: "#",
        },
        {
            id: 5,
            title: "Boss Babes",
            description:
                "In Ireland, women are taking the business world by storm. Starting businesses at a rate of 1400 per month, making up 50% of new business owners.",
            icon: fifth,
            link: "#",
        },
        {
            id: 6,
            title: "Education",
            description:
                "Women's educational attainment has improved significantly over time, and they are now prioritising their own needs and well-being more than before.",
            icon: sixth,
            link: "#",
        },
        {
            id: 7,
            title: "The 40% Club",
            description:
                "Despite progress in education and career opportunities, women in Europe still face a significant retirement income gap compared to men, highlighting the need for continued efforts towards financial equal...",
            icon: seventh,
            link: "#",
        },
    ]

    const handleReadResearch = (insightId: number, e: React.MouseEvent) => {
        e.preventDefault();
        setExpandedInsightIds((prev) =>
            prev.includes(insightId)
                ? prev.filter((id) => id !== insightId)
                : [...prev, insightId]
        );
    };

    const InsightCard = ({ insight }: { insight: typeof insights[0] }) => (
        <div
            key={insight.id}
            className="bg-pn_white border border-pn_gray-200 rounded-2xl overflow-hidden min-w-[340px] max-w-[340px] md:min-w-[380px] md:max-w-[380px] lg:min-w-[400px] lg:max-w-[400px] flex-shrink-0 flex flex-col"
        >
            <div className="h-[180px] overflow-hidden flex items-center justify-center bg-pn_paper-3">
                {insight.icon && <img src={insight.icon} alt={insight.title} />}
            </div>
            <div className="p-6 flex flex-col flex-grow">
                <h4 className="text-pn-headline-emphasis mb-2">{insight.title}</h4>
                <div className="overflow-hidden transition-all duration-300 ease-in-out" 
                     style={{ 
                         maxHeight: expandedInsightIds.includes(insight.id) ? '500px' : '0',
                         opacity: expandedInsightIds.includes(insight.id) ? 1 : 0
                     }}>
                    <p className="text-pn-callout-regular text-pn_gray-600 mb-4">{insight.description}</p>
                </div>
                <div className="mt-auto pt-4">
                    <button 
                        onClick={(e) => handleReadResearch(insight.id, e)}
                        className="text-pn_gray-950 text-pn-button-2 rounded-3xl inline-block ml-1 bg-pn_paper-3 px-4 py-2 hover:bg-pn_paper-4 transition-colors"
                    >
                        {expandedInsightIds.includes(insight.id) ? 'Hide Research' : 'Read Research'}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full">
            <div className="px-4 lg:px-[80px] py-6">
                <h2 className="text-pn-body-large-emphasis md:text-pn-headline-emphasis mb-4">Penny Insights</h2>

                {/* Mobile view - horizontal scroll for all cards */}
                <div className="md:hidden overflow-x-auto scrollbar-hide">
                    <div className="flex space-x-4 pb-4" style={{ minWidth: "min-content" }}>
                        {/* First card - intro */}
                        <div className="bg-pn_paper-3 rounded-2xl p-6 min-w-[340px] max-w-[340px] flex-shrink-0">
                            <div className="flex flex-col gap-y-4">
                                <h3 className="text-pn-large-title">
                                    Penny Insights
                                    <br />7 years of research in bite sized chunks
                                </h3>
                                <p className="text-pn-callout-regular text-pn_gray-600">
                                    Penny Insights shares seven years of wisdom from women around the world, shedding light on their
                                    financial perspectives. Our research, led by Lesley Tully, showcases the diverse experiences of
                                    women and their unique relationship with money. We aim to empower women and improve their financial
                                    well-being by sharing these valuable insights.
                                </p>
                            </div>
                        </div>

                        {/* Insight cards */}
                        {insights.map((insight) => (
                            <InsightCard key={insight.id} insight={insight} />
                        ))}
                    </div>
                </div>

                {/* Tablet view - horizontal scroll for all cards */}
                <div className="hidden md:block lg:hidden">
                    <div className="overflow-x-auto scrollbar-hide">
                        <div className="flex space-x-4 pb-4" style={{ minWidth: "min-content" }}>
                            {/* First card - intro */}
                            <div className="bg-pn_paper-3 rounded-2xl p-6 min-w-[380px] max-w-[380px] flex-shrink-0">
                                <div className="flex flex-col gap-y-4">
                                    <h3 className="text-pn-large-title">
                                        Penny Insights
                                        <br />7 years of research in bite sized chunks
                                    </h3>
                                    <p className="text-pn-callout-regular text-pn_gray-600">
                                        Penny Insights shares seven years of wisdom from women around the world, shedding light on their
                                        financial perspectives. Our research, led by Lesley Tully, showcases the diverse experiences of
                                        women and their unique relationship with money. We aim to empower women and improve their financial
                                        well-being by sharing these valuable insights.
                                    </p>
                                </div>
                            </div>

                            {/* Insight cards */}
                            {insights.map((insight) => (
                                <InsightCard key={insight.id} insight={insight} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Laptop view - fixed first card with horizontal scroll for insights only */}
                <div className="hidden lg:block">
                    <div className="flex gap-6">
                        {/* First card - intro (fixed) */}
                        <div className="bg-pn_paper-3 rounded-2xl p-6 min-w-[400px] max-w-[400px] flex-shrink-0">
                            <div className="flex flex-col gap-y-4">
                                <h3 className="text-pn-large-title">
                                    Penny Insights
                                    <br />7 years of research in bite sized chunks
                                </h3>
                                <p className="text-pn-callout-regular text-pn_gray-600">
                                    Penny Insights shares seven years of wisdom from women around the world, shedding light on their
                                    financial perspectives. Our research, led by Lesley Tully, showcases the diverse experiences of women
                                    and their unique relationship with money. We aim to empower women and improve their financial
                                    well-being by sharing these valuable insights.
                                </p>
                            </div>
                        </div>

                        {/* Insight cards container with horizontal scroll */}
                        <div className="overflow-x-auto scrollbar-hide flex-1">
                            <div className="flex space-x-4" style={{ minWidth: "min-content" }}>
                                {/* Insight cards */}
                                {insights.map((insight) => (
                                    <InsightCard key={insight.id} insight={insight} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PennyInsights